import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { 
  LayoutDashboard, Tag, Layers, Shirt, Palette, Scissors, Box, ClipboardList,
  Menu, X, Ruler, Droplets, Package, ArrowDownCircle, ArrowUpCircle, RefreshCw,
  FileText, BookOpen, Cog, Users, Play, BarChart3, Route, AlertTriangle, Sparkles,
  LogOut, ChevronDown, ChevronRight, Home, Factory, History, Truck, FolderTree, Link2,
  PackageCheck
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';

const menuItems = [
  {
    title: 'Inicio',
    icon: Home,
    path: '/',
    isHome: true
  },
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/produccion'
  },
  {
    title: 'Producción',
    icon: ClipboardList,
    children: [
      { title: 'Registros', icon: ClipboardList, path: '/produccion/registros' },
      { title: 'Modelos', icon: Box, path: '/produccion/modelos' },
      { title: 'Guías de Remisión', icon: Truck, path: '/produccion/guias-remision' },
      { title: 'Calidad/Merma', icon: AlertTriangle, path: '/produccion/calidad-merma' },
    ]
  },
  {
    title: 'Inventario',
    icon: Package,
    children: [
      { title: 'Stock General', icon: Package, path: '/produccion/inventario' },
      { title: 'Ingresos', icon: ArrowDownCircle, path: '/produccion/inventario/ingresos' },
      { title: 'Salidas', icon: ArrowUpCircle, path: '/produccion/inventario/salidas' },
      { title: 'Ajustes', icon: RefreshCw, path: '/produccion/inventario/ajustes' },
      { title: 'Rollos', icon: Layers, path: '/produccion/inventario/rollos' },
      { title: 'Kardex', icon: BookOpen, path: '/produccion/inventario/kardex' },
      { title: 'Por Recibir', icon: Truck, path: '/produccion/inventario/pendientes' },
      { title: 'Recepciones', icon: PackageCheck, path: '/produccion/inventario/recepciones' },
    ]
  },
  {
    title: 'Maestros',
    icon: Cog,
    children: [
      { title: 'Servicios', icon: Cog, path: '/produccion/maestros/servicios' },
      { title: 'Personas', icon: Users, path: '/produccion/maestros/personas' },
      { title: 'Rutas', icon: Route, path: '/produccion/maestros/rutas' },
      { title: 'Movimientos', icon: Play, path: '/produccion/maestros/movimientos' },
    ]
  },
  {
    title: 'Catálogos',
    icon: FolderTree,
    children: [
      { title: 'Marcas', icon: Tag, path: '/produccion/marcas' },
      { title: 'Tipos', icon: Layers, path: '/produccion/tipos' },
      { title: 'Entalles', icon: Shirt, path: '/produccion/entalles' },
      { title: 'Telas', icon: Palette, path: '/produccion/telas' },
      { title: 'Hilos', icon: Scissors, path: '/produccion/hilos' },
      { title: 'Hilos Específicos', icon: Sparkles, path: '/produccion/hilos-especificos' },
      { title: 'Tallas', icon: Ruler, path: '/produccion/tallas-catalogo' },
      { title: 'Colores', icon: Droplets, path: '/produccion/colores-catalogo' },
      { title: 'Colores Generales', icon: Palette, path: '/produccion/colores-generales' },
    ]
  },
  {
    title: 'Reportes',
    icon: BarChart3,
    children: [
      { title: 'Productividad', icon: BarChart3, path: '/produccion/maestros/productividad' },
      { title: 'Movimientos', icon: FileText, path: '/produccion/reportes/movimientos' },
      { title: 'Mermas', icon: AlertTriangle, path: '/produccion/reportes/mermas' },
    ]
  },
  {
    title: 'Integración',
    icon: Link2,
    path: '/produccion/integracion'
  }
];

// Admin menu items
const adminMenuItems = [
  {
    title: 'Administración',
    icon: History,
    adminOnly: true,
    children: [
      { title: 'Historial Actividad', icon: History, path: '/produccion/historial' },
    ]
  }
];

const MenuItem = ({ item, isOpen, toggleOpen, isMobile, onNavigate, userRole }) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path === location.pathname || 
    (hasChildren && item.children.some(child => child.path === location.pathname));
  
  // Check if item is admin only
  if (item.adminOnly && userRole !== 'admin') {
    return null;
  }
  
  const Icon = item.icon;

  // Special styling for Home button
  if (item.isHome) {
    return (
      <Link
        to={item.path}
        onClick={isMobile ? onNavigate : undefined}
        className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all mb-3 border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
      >
        <Icon className="w-5 h-5" />
        <span>{item.title}</span>
      </Link>
    );
  }

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={toggleOpen}
          className={cn(
            "w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-all",
            isActive 
              ? "bg-emerald-50 text-emerald-700 font-medium" 
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5" />
            <span>{item.title}</span>
          </div>
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {isOpen && (
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-100 pl-3">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const isChildActive = child.path === location.pathname;
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={isMobile ? onNavigate : undefined}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all",
                    isChildActive 
                      ? "bg-emerald-100 text-emerald-700 font-medium" 
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <ChildIcon className="w-4 h-4" />
                  <span>{child.title}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={isMobile ? onNavigate : undefined}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all mb-1",
        isActive 
          ? "bg-emerald-100 text-emerald-700 font-medium" 
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      )}
    >
      <Icon className="w-5 h-5" />
      <span>{item.title}</span>
    </Link>
  );
};

const LayoutProduccion = ({ children }) => {
  const { user, logout } = useApp();
  const [openMenus, setOpenMenus] = useState({ 'Producción': true, 'Catálogos': true });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = (title) => {
    setOpenMenus(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get user initials
  const getUserInitials = () => {
    if (!user?.nombre) return user?.username?.[0]?.toUpperCase() || 'U';
    const parts = user.nombre.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.nombre[0].toUpperCase();
  };

  // All menu items combined
  const allMenuItems = [...menuItems, ...adminMenuItems];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-30 h-14">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Factory className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-800 hidden sm:inline">Producción</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-emerald-600 text-white text-sm">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center gap-2 p-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-emerald-600 text-white text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium">{user?.nombre || user?.username || 'Usuario'}</p>
                    <p className="text-xs text-gray-500">{user?.rol || 'usuario'}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/')}>
                  <Home className="w-4 h-4 mr-2" />
                  Ir al Inicio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/finanzas')}>
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Ir a Finanzas
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-14 left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transition-transform duration-300",
        "lg:translate-x-0",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <nav className="p-4 h-full overflow-y-auto">
          {allMenuItems.map((item) => (
            <MenuItem
              key={item.title}
              item={item}
              isOpen={openMenus[item.title]}
              toggleOpen={() => toggleMenu(item.title)}
              isMobile={mobileMenuOpen}
              onNavigate={closeMobileMenu}
              userRole={user?.rol}
            />
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 min-h-screen">
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default LayoutProduccion;
